import styled from '@emotion/styled';
import InfiniteScroll from 'aquilla/core/organisms/InfiniteScroll';
import Skeleton from 'aquilla/core/atoms/Skeleton';
import useCuratedPlaylist from '@api/hooks/curatedPlaylist/useCuratedPlaylist';
import {
  getAuthorName,
  getPlaylistThumbnail,
  LANGUAGE_MAP
} from '@cont/Content/FreeLesson/utils';
import { getLessonString, getUrl } from '@cont/FeedTypeBlock/CuratedPlayList';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import { useSWRConfig } from 'swr';
import EducatorPlaylistCard from 'aquilla/core/organisms/EducatorPlaylistCard';
import Image from '@base/Image';
import { mediaQuery, Spacings } from '@styles/index';
import { segment } from '@actions/analytics';
import { useRouter } from 'next/router';
import {
  EDUCATOR_PROFILE_PLAYLIST_SEE_ALL,
  PLAYLIST_CARD_CLICKED,
  SPECIAL_CLASS_PLAYLIST_SEE_ALL
} from '@cont/Content/FreeLesson/constants/events';

const Container = styled.div`
  grid-column: span 18;
  ${mediaQuery.uptoMobile} {
    padding-bottom: ${Spacings.SPACING_4B};
  }
`;

export const StyledEducatorPlaylistCard = styled(EducatorPlaylistCard)`
  max-height: 282px;
  width: 272px;
  max-width: 272px;
  .aquilla-educator-playlist-educator-thumbnail-wrapper {
    height: 155px;
    width: 240px;
    > div:first-child {
      height: 155px;
    }
  }
  .aquilla-educator-playlist-details-wrapper {
    gap: 8px;
    .aquilla-educator-playlist-info {
      h6 {
        -webkit-line-clamp: 2;
      }
    }
  }
  ${mediaQuery.uptoMobile} {
    width: 328px;
    max-width: 328px;
    max-height: 324px;
    > div:first-child {
      width: 296px;
    }
  }
`;

const StyledInfiniteScroll = styled(InfiniteScroll)`
  display: grid;
  grid-gap: ${Spacings.SPACING_4B};
  grid-template-columns: ${({ $cardsPerRow }) => {
    return `repeat(${$cardsPerRow}, 1fr)`;
  }};
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const GhostCardContainer = styled(Skeleton)`
  display: grid;
  border-radius: ${Spacings.SPACING_2B};
  height: 324px;
  width: 272px;
  ${mediaQuery.uptoMobile} {
    width: 328px;
  }
`;

const EduThumbWrapper = styled.div`
  position: relative;
  justify-content: center;
`;

const EduImage = styled(Image)`
  object-fit: contain;
`;

const ImageWrapper = styled.div`
  position: absolute;
  width: 170px;
  height: 155px;
  bottom: -${Spacings.SPACING_1B};
  left: 50%;
  transform: translate(-50%, 0);
  max-width: 90%;
  max-height: 100%;
  ${mediaQuery.uptoMobile} {
    left: 40%;
  }
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 240px;
  height: 155px;
  ${mediaQuery.uptoMobile} {
    width: 296px;
  }
`;

const Thumbnail = styled(Image)`
  object-fit: cover;
`;

export const EduThumbnail = ({ url, topicName }) => {
  const thumbnail = getPlaylistThumbnail(topicName);

  return (
    <EduThumbWrapper>
      <ThumbnailWrapper>
        <Thumbnail src={thumbnail} layout="fill" alt="edu-thumbnail" />
      </ThumbnailWrapper>
      <ImageWrapper>
        <EduImage src={url} layout="fill" alt="edu-image" />
      </ImageWrapper>
    </EduThumbWrapper>
  );
};

const GhostCard = () => {
  return <GhostCardContainer height="292px" variant="rect" />;
};
const CuratedPlaylistCards = ({
  url,
  cardsPerRow,
  isEducator,
  educatorUID
}) => {
  const { fallback = {} } = useSWRConfig();
  const { query } = useRouter();
  const { data, isValidating, setSize } = useCuratedPlaylist(url, {
    fallbackData: fallback[url]
  });

  const handleCardClick = (playlistData) => {
    const author = playlistData?.authors?.[0] || {};
    const authorFullName = getAuthorName(author);
    const uid = author?.uid || educatorUID;
    segment.track(PLAYLIST_CARD_CLICKED, {
      current_screen: isEducator
        ? EDUCATOR_PROFILE_PLAYLIST_SEE_ALL
        : SPECIAL_CLASS_PLAYLIST_SEE_ALL,
      goal_uid: query?.goalUID ?? 'NA',
      is_new_user: 'NA',
      playlist_name: playlistData?.name ?? 'NA',
      playlist_uid: playlistData?.uid ?? 'NA',
      educator_uid: uid ?? 'NA',
      educator_name: authorFullName
    });
  };

  const renderItems = ({ item: playlistData }) => {
    const author = playlistData?.authors?.[0] || {};
    const { color } = getTopicGroupAssetObj(playlistData?.topicGroupName);
    const authorFullName = getAuthorName(author);
    const { href } = getUrl(playlistData);
    return (
      <StyledEducatorPlaylistCard
        onClick={() => handleCardClick(playlistData)}
        key={playlistData.uid}
        educatorName={authorFullName}
        title={playlistData.name}
        topicColor={color}
        topicName={playlistData.topicGroupName}
        lessons={getLessonString(playlistData.lessonsCount)}
        langLabel={LANGUAGE_MAP[playlistData.language]?.languageText || 'EN'}
        thumbnail={() => (
          <EduThumbnail
            url={author.avatarV2 || author.oldAvatar}
            color={color}
            topicName={playlistData.topicGroupName}
          />
        )}
        href={href}
        educatorLink={`/@${author.username}`}
      />
    );
  };
  const loadMoreFeedBlocks = async () => {
    if (!isValidating && data?.next) {
      setSize((nPages) => nPages + 1);
    }
  };

  return (
    <Container>
      <StyledInfiniteScroll
        $cardsPerRow={cardsPerRow}
        loadMore={loadMoreFeedBlocks}
        items={data?.results}
        renderItem={renderItems}
        LoaderSkeleton={<GhostCard />}
        isFetching={isValidating}
      />
    </Container>
  );
};

export default CuratedPlaylistCards;
